import React, { createContext, useState, useMemo } from 'react';
import { MarketAggregatesState } from './types';
import {
  MarketAggregatesContextType,
  MarketAggregatesProviderProps,
  INITIAL_STATE,
} from './constants';
import { useChartDataOperations } from './chartDataOperations';
import { useModelStateOperations } from './modelStateOperations';
import { useModelFetchingOperations } from './modelFetchingOperations';
import { useAvailableZonesOperations } from './availableZonesOperations';

export const MarketAggregatesContext = createContext<MarketAggregatesContextType | undefined>(
  undefined,
);

// Re-export the hook for backward compatibility
export { useMarketAggregates } from './useMarketAggregates';

export const MarketAggregatesProvider: React.FC<MarketAggregatesProviderProps> = ({
  children,
  initialState,
}) => {
  const [state, setState] = useState<MarketAggregatesState>(() => ({
    ...INITIAL_STATE,
    ...initialState,
  }));

  const { fetchChartData, clearChartData } = useChartDataOperations(state, setState);
  const {
    toggleModelSelection,
    updateModelRuns,
    resetAllSelections,
    setWeightType,
    setMaxLeadTimeHours,
  } = useModelStateOperations(setState);
  const { fetchAvailableZones } = useAvailableZonesOperations(state, setState);
  useModelFetchingOperations(state, setState);

  const contextValue: MarketAggregatesContextType = useMemo(
    () => ({
      state,
      toggleModelSelection,
      updateModelRuns,
      resetAllSelections,
      setWeightType,
      setMaxLeadTimeHours,
      fetchAvailableZones,
      fetchChartData,
      clearChartData,
    }),
    [
      state,
      toggleModelSelection,
      updateModelRuns,
      resetAllSelections,
      setWeightType,
      setMaxLeadTimeHours,
      fetchAvailableZones,
      fetchChartData,
      clearChartData,
    ],
  );

  return (
    <MarketAggregatesContext.Provider value={contextValue}>
      {children}
    </MarketAggregatesContext.Provider>
  );
};
